import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  useInstructorCohorts,
  useCreateCohort,
  useUpdateCohort,
  useDeleteCohort,
  Cohort,
} from "@/hooks/useCohorts";
import { useInstructorCurricula } from "@/hooks/useCurricula";
import {
  Plus,
  Edit,
  Trash2,
  Users,
  CalendarIcon,
  Lock,
  Unlock,
} from "lucide-react";
import { cn } from "@/lib/utils";

export function CohortTab() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: cohorts, isLoading } = useInstructorCohorts();
  const { data: curricula } = useInstructorCurricula();
  const createMutation = useCreateCohort();
  const updateMutation = useUpdateCohort();
  const deleteMutation = useDeleteCohort();

  const [createOpen, setCreateOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingCohort, setEditingCohort] = useState<Cohort | null>(null);

  // Create form state
  const [name, setName] = useState("");
  const [curriculumId, setCurriculumId] = useState("");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [gatingEnabled, setGatingEnabled] = useState(true);

  // Edit form state
  const [editName, setEditName] = useState("");
  const [editCurriculumId, setEditCurriculumId] = useState("");
  const [editStartDate, setEditStartDate] = useState<Date | undefined>();
  const [editEndDate, setEditEndDate] = useState<Date | undefined>();
  const [editGatingEnabled, setEditGatingEnabled] = useState(false);
  const [editIsActive, setEditIsActive] = useState(true);

  const resetCreateForm = () => {
    setName("");
    setCurriculumId("");
    setStartDate(undefined);
    setEndDate(undefined);
    setGatingEnabled(false);
  };

  const openEdit = (cohort: Cohort) => {
    setEditingCohort(cohort);
    setEditName(cohort.name);
    setEditCurriculumId(cohort.curriculum_id);
    setEditStartDate(cohort.start_date ? new Date(cohort.start_date) : undefined);
    setEditEndDate(cohort.end_date ? new Date(cohort.end_date) : undefined);
    setEditGatingEnabled(cohort.gating_enabled);
    setEditIsActive(cohort.is_active);
  };

  const handleCreate = async () => {
    if (!name.trim() || !curriculumId || !startDate) return;
    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        curriculum_id: curriculumId,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: endDate ? format(endDate, "yyyy-MM-dd") : null,
        gating_enabled: gatingEnabled,
      });
      toast({ title: "Cohort created!", description: "Your new cohort is ready." });
      setCreateOpen(false);
      resetCreateForm();
    } catch {
      toast({ title: "Error", description: "Failed to create cohort.", variant: "destructive" });
    }
  };

  const handleUpdate = async () => {
    if (!editingCohort || !editName.trim() || !editCurriculumId) return;
    try {
      await updateMutation.mutateAsync({
        id: editingCohort.id,
        name: editName.trim(),
        curriculum_id: editCurriculumId,
        start_date: editStartDate ? format(editStartDate, "yyyy-MM-dd") : null,
        end_date: editEndDate ? format(editEndDate, "yyyy-MM-dd") : null,
        gating_enabled: editGatingEnabled,
        is_active: editIsActive,
      });
      toast({ title: "Cohort updated!" });
      setEditingCohort(null);
    } catch {
      toast({ title: "Error", description: "Failed to update cohort.", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await deleteMutation.mutateAsync(deletingId);
      toast({ title: "Cohort deleted" });
      setDeletingId(null);
    } catch {
      toast({ title: "Error", description: "Failed to delete cohort.", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Cohort
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Cohort</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Cohort Name</Label>
                <Input
                  placeholder="e.g. Spring 2026 Cohort"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Curriculum</Label>
                <Select value={curriculumId} onValueChange={setCurriculumId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a curriculum" />
                  </SelectTrigger>
                  <SelectContent>
                    {curricula?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>End Date (optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={gatingEnabled}
                  onCheckedChange={setGatingEnabled}
                />
                <Label>Lock modules until manually unlocked</Label>
              </div>
              <Button
                onClick={handleCreate}
                disabled={!name.trim() || !curriculumId || !startDate || createMutation.isPending}
                className="w-full"
              >
                {createMutation.isPending ? "Creating..." : "Create Cohort"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : cohorts && cohorts.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cohorts.map((cohort) => (
            <Card key={cohort.id} className="card-hover cursor-pointer" onClick={() => navigate(`/instructor/cohorts/${cohort.id}`)}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg line-clamp-1">
                    {cohort.name}
                  </CardTitle>
                  <div className="flex gap-1">
                    <Badge variant={cohort.is_active ? "default" : "secondary"}>
                      {cohort.is_active ? "Active" : "Inactive"}
                    </Badge>
                    <Badge variant={cohort.gating_enabled ? "outline" : "secondary"}>
                      {cohort.gating_enabled ? (
                        <><Lock className="h-3 w-3 mr-1" /> Gated</>
                      ) : (
                        <><Unlock className="h-3 w-3 mr-1" /> Open</>
                      )}
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {cohort.curriculum_title}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <CalendarIcon className="h-4 w-4" />
                    {cohort.start_date
                      ? format(new Date(cohort.start_date), "MMM d, yyyy")
                      : "No start date"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {cohort.member_count || 0} members
                  </span>
                </div>

                <div className="flex items-center justify-between pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); navigate(`/instructor/cohorts/${cohort.id}`); }}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Manage Members
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); openEdit(cohort); }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); setDeletingId(cohort.id); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">No cohorts yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first cohort to group learners
            </p>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Cohort
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Edit Cohort Dialog */}
      <Dialog open={!!editingCohort} onOpenChange={(open) => !open && setEditingCohort(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Cohort</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Cohort Name</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Curriculum</Label>
              <Select value={editCurriculumId} onValueChange={setEditCurriculumId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a curriculum" />
                </SelectTrigger>
                <SelectContent>
                  {curricula?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !editStartDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editStartDate ? format(editStartDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editStartDate}
                    onSelect={setEditStartDate}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>End Date (optional)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !editEndDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editEndDate ? format(editEndDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editEndDate}
                    onSelect={setEditEndDate}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={editGatingEnabled}
                onCheckedChange={setEditGatingEnabled}
              />
              <Label>Lock modules until manually unlocked</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={editIsActive}
                onCheckedChange={setEditIsActive}
              />
              <Label>Active</Label>
            </div>
            <Button
              onClick={handleUpdate}
              disabled={!editName.trim() || !editCurriculumId || updateMutation.isPending}
              className="w-full"
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingId} onOpenChange={() => setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Cohort</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting {cohorts?.find(c => c.id === deletingId)?.name ? `"${cohorts.find(c => c.id === deletingId)!.name}"` : "this cohort"} will remove all member enrollments associated with this cohort. This cannot be undone. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
